import { useRef, useState } from 'react';
import { ActionIcon, Box, Group, Loader, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconDots, IconTrash, IconX } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Conversation } from '~api';
import { Cmp, logger } from '~portal/lib';
import { useConversationStore } from '~portal/stores';
import { touchTarget } from '~portal/styles/touch-target.css';
import * as styles from './ChatListItem.css';
import { conversationTitle } from './conversation-title';
import { useDeleteConversation } from './use-delete-conversation';
import { useRenameConversation } from './use-rename-conversation';

export type ChatListItemProps = {
  id: string;
  updatedAt: string;
  summary?: string | null;
  /** Called after navigation completes (e.g., to close drawer) */
  onClick?: () => void;
};

const ACTION_ICON_SIZE = 36;

/** Diameter of the row's status mark — spinner and active-agent dot alike. */
const STATUS_MARK_SIZE = 12;

/**
 * @desc Open the rename field on the *start* of the title, not its end. A focused input scrolls
 * to its caret, so the browser's default (caret at the end) hid the beginning of a long name —
 * the half that identifies the thread. Selecting the whole title is the ordinary rename
 * affordance (Finder, VS Code): it anchors the view at character zero and makes "type over it"
 * the one-keystroke path, while an arrow key still drops the caret in to edit.
 *
 * Module-level so the ref identity is stable — a callback ref defined in the body would re-run,
 * and re-select, on every render.
 */
const focusRenameField = (input: HTMLInputElement | null) => {
  if (!input) return;
  input.focus();
  input.select();
  input.scrollLeft = 0;
};

/**
 * @desc A Charlie sidebar thread row: a borderless text row that becomes a rounded pill when
 * selected, with a trailing ⋯ that expands the row into its action state: an editable title
 * field plus delete and collapse buttons. Sharing and saving are not built yet, so they are
 * absent rather than shown disabled.
 */
export const ChatListItem: Cmp<ChatListItemProps> = function ({ id, updatedAt, summary, onClick }) {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const currentConversationId = 'conversationId' in params ? params.conversationId : undefined;
  const isActive = currentConversationId === id;
  const [expanded, setExpanded] = useState(false);

  // Check if this conversation's agent is currently processing (reactive)
  const isAgentActive = useConversationStore((state) => state.activeAgents.has(id));

  /**
   * The row the reader has just clicked, whose conversation the client does not hold yet. It is
   * derived rather than announced: `useConversation` calls the same thing "loading" from exactly
   * these two facts (this is the open conversation, and the store has no context for it), so a
   * flag pushed into the store would be a second copy of a state that is already unambiguous —
   * and one that could disagree with the transcript's own spinner.
   */
  const isFetching = useConversationStore((state) => state.contexts[id] === undefined) && isActive;

  /**
   * Both status marks share one fixed slot beside the title, so a row never changes width when
   * the spinner gives way to the dot (a click on a cold conversation shows both in turn).
   */
  const statusMark = isFetching ? (
    <Box className={styles.statusSlot}>
      <Loader size={STATUS_MARK_SIZE} type="oval" aria-label="Loading conversation" />
    </Box>
  ) : isAgentActive ? (
    <Box className={styles.statusSlot}>
      <Box className={styles.activeAgentDot} />
    </Box>
  ) : null;
  const { confirmDelete } = useDeleteConversation();
  const { rename } = useRenameConversation();

  /**
   * The first thing the user said in this thread, when the client has the thread loaded.
   * `conversations.list` is a metadata-only endpoint — its Mongo projection deliberately
   * excludes `contexts` — so there is no prompt text in the listing to fall back to. The
   * store is the only client-side source, and it covers the case that actually matters: a
   * chat you have just had, sitting at the top of the sidebar with no summary yet. A thread
   * never opened in this browser keeps the placeholder, which is the pre-existing behaviour.
   */
  const firstPrompt = useConversationStore((state) => state.grouped[id]?.[0]?.prompt);

  const label = conversationTitle(summary, firstPrompt);

  // Draft title for the inline rename field, seeded each time the action state opens.
  const [draft, setDraft] = useState(label);
  /**
   * Escape blurs the field to leave it, and the blur handler would otherwise commit the draft
   * it is meant to discard. A ref rather than state: the blur is dispatched synchronously, so a
   * state update would not be visible to it.
   */
  const cancelledRef = useRef(false);

  /** Leave the action state without committing: Escape, from the field or from either button. */
  const cancelActions = () => {
    cancelledRef.current = true;
    setDraft(label);
    setExpanded(false);
  };

  const openActions = () => {
    setDraft(label);
    setExpanded(true);
  };

  /** Commit the draft. An empty draft is not a rename — restore the current title instead. */
  const commitRename = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft(label);
      return;
    }
    setDraft(trimmed);
    rename(id, trimmed, label);
  };

  const handleClick = () => {
    if (expanded) return;

    navigate({ to: '/chat/$conversationId', params: { conversationId: id } })
      .then(() => onClick?.())
      .catch((err) => {
        logger.error('[ChatListItem] Navigation failed', {
          conversationId: id,
          error: err instanceof Error ? err.message : String(err),
        });
      });
  };

  const formattedDate = new Date(updatedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const rowClass = [
    styles.row,
    isActive && !expanded ? styles.rowActive : null,
    expanded ? styles.rowExpanded : null,
  ]
    .filter(Boolean)
    .join(' ');

  if (expanded) {
    return (
      <Stack
        gap="xs"
        className={rowClass}
        data-agent-active={isAgentActive}
        data-fetching={isFetching}
        // Escape closes the card from anywhere inside it — the field, the delete button, the
        // close button — so the keyboard has the same exit the close button offers the mouse.
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          cancelActions();
        }}
      >
        <Group gap="xs" wrap="nowrap">
          {statusMark}
          <TextInput
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            maxLength={Conversation.TitleMaxLength}
            size="sm"
            variant="unstyled"
            ref={focusRenameField}
            aria-label="Conversation name"
            title={formattedDate}
            classNames={{ root: styles.renameField, input: styles.renameInput }}
            onBlur={() => {
              if (cancelledRef.current) {
                cancelledRef.current = false;
                setDraft(label);
                return;
              }
              commitRename();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitRename();
                // The field unmounts with the action state; make any trailing blur a no-op.
                cancelledRef.current = true;
                setExpanded(false);
                return;
              }
              // Escape is handled by the card, which closes it as well as discarding the draft.
            }}
          />
        </Group>

        <Group gap="xs" wrap="nowrap" justify="flex-end">
          <Tooltip label="Delete conversation" withArrow>
            <ActionIcon
              size={ACTION_ICON_SIZE}
              className={touchTarget}
              radius="xl"
              variant="filled"
              color="red"
              aria-label="Delete conversation"
              onClick={() => confirmDelete(id)}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Close" withArrow>
            <ActionIcon
              size={ACTION_ICON_SIZE}
              className={touchTarget}
              radius="xl"
              variant="filled"
              color="blue.7"
              aria-label="Close conversation actions"
              onClick={() => setExpanded(false)}
            >
              <IconX size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Stack>
    );
  }

  return (
    <Group
      gap="xs"
      wrap="nowrap"
      justify="space-between"
      className={rowClass}
      data-agent-active={isAgentActive}
      data-fetching={isFetching}
      role="button"
      tabIndex={0}
      // Named explicitly rather than left to the subtree: the row also carries a `title`
      // attribute (the timestamp) and a status mark, so what a screen reader announced varied
      // with what happened to be in the row at the time. The thread's title is the row.
      aria-label={label}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleClick();
      }}
    >
      {/* The status mark and the ⋯ stay vertically centred against the clamped two-line block:
          both Groups centre their children by default, so nothing here pins them to a line. */}
      <Group gap={8} wrap="nowrap" miw={0} className={styles.titleBlock}>
        {statusMark}
        <Text size="sm" className={styles.title} title={formattedDate}>
          {label}
        </Text>
      </Group>

      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        radius="xl"
        className={`${styles.menuButton} ${touchTarget}`}
        aria-label={`Conversation actions for ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          openActions();
        }}
      >
        <IconDots size={18} />
      </ActionIcon>
    </Group>
  );
};
